import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  query,
  where,
  limit,
  GeoPoint,
  Unsubscribe,
  type FieldValue,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LocationCoordinates } from './location';

export type LocationShare = {
  id: string;
  userId: string;
  sharerName?: string;
  sharedWith: string[]; // User IDs or email addresses
  tripId?: string; // If sharing with trip companions
  isActive: boolean;
  expiresAt?: Date;
  createdAt: Date;
  lastLocation?: LocationCoordinates;
  lastUpdated?: Date;
};

export type FirestoreLocationShare = {
  userId: string;
  sharerName?: string;
  sharedWith: string[];
  tripId?: string;
  isActive: boolean;
  expiresAt?: Timestamp | string | null;
  createdAt: Timestamp | string | FieldValue;
  lastLocation?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  lastUpdated?: Timestamp | string | FieldValue;
};

function toFiniteCoord(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function lastLocationFromFirestore(val: unknown): LocationCoordinates | undefined {
  if (val == null || typeof val !== 'object') {
    return undefined;
  }
  if (val instanceof GeoPoint) {
    return { latitude: val.latitude, longitude: val.longitude };
  }
  const o = val as Record<string, unknown>;
  const latitude = toFiniteCoord(o.latitude ?? o.lat);
  const longitude = toFiniteCoord(o.longitude ?? o.lng ?? o.long);
  if (latitude === undefined || longitude === undefined) {
    return undefined;
  }
  const accuracy = toFiniteCoord(o.accuracy);
  return {
    latitude,
    longitude,
    ...(accuracy !== undefined ? { accuracy } : {}),
  };
}

/** True if the share should be treated as active on this device right now (wall-clock expiration). */
export function isLocationShareEffective(share: LocationShare | null | undefined): boolean {
  if (!share || !share.isActive) {
    return false;
  }
  if (share.expiresAt && share.expiresAt.getTime() <= Date.now()) {
    return false;
  }
  return true;
}

function locationShareFromFirestoreDoc(
  docId: string,
  data: FirestoreLocationShare
): LocationShare | null {
  if (!data.isActive) {
    return null;
  }
  if (data.expiresAt) {
    const expiresAt =
      data.expiresAt instanceof Timestamp ? data.expiresAt.toDate() : new Date(data.expiresAt);
    if (expiresAt < new Date()) {
      return null;
    }
  }

  return {
    id: docId,
    userId: data.userId,
    sharerName: typeof data.sharerName === 'string' ? data.sharerName : undefined,
    sharedWith: data.sharedWith,
    tripId: data.tripId,
    isActive: data.isActive,
    expiresAt: data.expiresAt
      ? data.expiresAt instanceof Timestamp
        ? data.expiresAt.toDate()
        : new Date(data.expiresAt)
      : undefined,
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
    lastLocation: lastLocationFromFirestore(data.lastLocation),
    lastUpdated: data.lastUpdated
      ? data.lastUpdated instanceof Timestamp
        ? data.lastUpdated.toDate()
        : new Date(data.lastUpdated as string)
      : undefined,
  };
}

/**
 * Create a location share
 */
export const createLocationShare = async (
  userId: string,
  sharedWith: string[],
  tripId?: string,
  expiresInHours?: number,
  initialLocation?: LocationCoordinates,
  sharerName?: string,
): Promise<{ success: boolean; error?: string; shareId?: string }> => {
  try {
    if (sharedWith.length === 0) {
      return { success: false, error: 'Must specify at least one recipient' };
    }

    // Check if user already has an active share
    const existingShare = await getActiveLocationShare(userId);
    if (existingShare) {
      // Update existing share instead of creating new one
      const res = await updateLocationShare(userId, existingShare.id, {
        sharedWith,
        tripId,
        expiresInHours,
        sharerName,
        ...(initialLocation ? { initialLocation } : {}),
      });
      return res.success ? { ...res, shareId: existingShare.id } : res;
    }

    const shareRef = doc(collection(db, 'locationShares'));
    const expiresAt = expiresInHours
      ? new Date(Date.now() + expiresInHours * 60 * 60 * 1000)
      : undefined;

    const share: FirestoreLocationShare = {
      userId,
      ...(typeof sharerName === 'string' && sharerName.trim().length > 0
        ? { sharerName: sharerName.trim() }
        : {}),
      sharedWith,
      tripId,
      isActive: true,
      expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : null,
      createdAt: serverTimestamp(),
      ...(initialLocation
        ? {
            lastLocation: {
              latitude: initialLocation.latitude,
              longitude: initialLocation.longitude,
              ...(initialLocation.accuracy != null ? { accuracy: initialLocation.accuracy } : {}),
            },
            lastUpdated: serverTimestamp(),
          }
        : {}),
    };

    await setDoc(shareRef, share);

    return { success: true, shareId: shareRef.id };
  } catch (error) {
    console.error('[LocationSharing] Error creating location share:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create location share',
    };
  }
};

/**
 * Update location share
 */
export const updateLocationShare = async (
  userId: string,
  shareId: string,
  updates: {
    sharedWith?: string[];
    tripId?: string;
    expiresInHours?: number;
    initialLocation?: LocationCoordinates;
    sharerName?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const shareRef = doc(db, 'locationShares', shareId);
    const shareDoc = await getDoc(shareRef);

    if (!shareDoc.exists()) {
      return { success: false, error: 'Location share not found' };
    }

    const shareData = shareDoc.data() as FirestoreLocationShare;
    if (shareData.userId !== userId) {
      return { success: false, error: 'Unauthorized' };
    }

    const updateData: Partial<FirestoreLocationShare> = {};

    if (updates.sharedWith !== undefined) {
      updateData.sharedWith = updates.sharedWith;
    }
    if (updates.tripId !== undefined) {
      updateData.tripId = updates.tripId;
    }
    if (updates.expiresInHours !== undefined) {
      updateData.expiresAt = updates.expiresInHours
        ? Timestamp.fromDate(new Date(Date.now() + updates.expiresInHours * 60 * 60 * 1000))
        : null;
    }
    if (updates.sharerName !== undefined) {
      const trimmedSharerName = updates.sharerName.trim();
      updateData.sharerName = trimmedSharerName.length > 0 ? trimmedSharerName : '';
    }
    if (updates.initialLocation) {
      updateData.lastLocation = {
        latitude: updates.initialLocation.latitude,
        longitude: updates.initialLocation.longitude,
        ...(updates.initialLocation.accuracy != null
          ? { accuracy: updates.initialLocation.accuracy }
          : {}),
      };
      updateData.lastUpdated = serverTimestamp();
    }

    await updateDoc(shareRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('[LocationSharing] Error updating location share:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update location share',
    };
  }
};

/**
 * Update location on a share document.
 * Pass `shareId` when you already know it (avoids races with getActiveLocationShare right after create).
 */
export const updateLocationInShare = async (
  userId: string,
  location: LocationCoordinates,
  shareId?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    let resolvedId = shareId;
    if (!resolvedId) {
      const share = await getActiveLocationShare(userId);
      if (!share) {
        return { success: false, error: 'No active location share' };
      }
      resolvedId = share.id;
    }

    const shareRef = doc(db, 'locationShares', resolvedId);
    const shareDoc = await getDoc(shareRef);
    if (!shareDoc.exists()) {
      return { success: false, error: 'Location share not found' };
    }

    const shareData = shareDoc.data() as FirestoreLocationShare;
    if (shareData.userId !== userId) {
      return { success: false, error: 'Unauthorized' };
    }
    if (!shareData.isActive) {
      return { success: false, error: 'No active location share' };
    }
    if (shareData.expiresAt) {
      const expiresAt =
        shareData.expiresAt instanceof Timestamp
          ? shareData.expiresAt.toDate()
          : new Date(shareData.expiresAt);
      if (expiresAt.getTime() <= Date.now()) {
        return { success: false, error: 'Location share expired' };
      }
    }

    await updateDoc(shareRef, {
      lastLocation: {
        latitude: location.latitude,
        longitude: location.longitude,
        ...(location.accuracy != null ? { accuracy: location.accuracy } : {}),
      },
      lastUpdated: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('[LocationSharing] Error updating location in share:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update location',
    };
  }
};

/**
 * Stop location sharing
 */
export const stopLocationShare = async (
  userId: string,
  shareId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const shareRef = doc(db, 'locationShares', shareId);
    const shareDoc = await getDoc(shareRef);

    if (!shareDoc.exists()) {
      return { success: false, error: 'Location share not found' };
    }

    const shareData = shareDoc.data() as FirestoreLocationShare;
    if (shareData.userId !== userId) {
      return { success: false, error: 'Unauthorized' };
    }

    await updateDoc(shareRef, {
      isActive: false,
    });

    return { success: true };
  } catch (error) {
    console.error('[LocationSharing] Error stopping location share:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to stop location share',
    };
  }
};

/**
 * Get active location share for user
 */
export const getActiveLocationShare = async (
  userId: string
): Promise<LocationShare | null> => {
  try {
    const sharesRef = collection(db, 'locationShares');
    const shareQuery = query(
      sharesRef,
      where('userId', '==', userId),
      where('isActive', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(shareQuery);
    if (snapshot.empty) {
      return null;
    }

    const docSnap = snapshot.docs[0];
    const data = docSnap.data() as FirestoreLocationShare;

    // Check expiration before returning
    if (data.expiresAt) {
      const expiresAt =
        data.expiresAt instanceof Timestamp ? data.expiresAt.toDate() : new Date(data.expiresAt);
      if (expiresAt < new Date()) {
        return null;
      }
    }

    return {
      id: docSnap.id,
      userId: data.userId,
      sharerName: typeof data.sharerName === 'string' ? data.sharerName : undefined,
      sharedWith: data.sharedWith,
      tripId: data.tripId,
      isActive: data.isActive,
      expiresAt: data.expiresAt
        ? data.expiresAt instanceof Timestamp
          ? data.expiresAt.toDate()
          : new Date(data.expiresAt)
        : undefined,
      createdAt:
        data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : typeof data.createdAt === 'string' || typeof data.createdAt === 'number'
            ? new Date(data.createdAt)
            : new Date(),
      lastLocation: lastLocationFromFirestore(data.lastLocation),
      lastUpdated: data.lastUpdated
        ? data.lastUpdated instanceof Timestamp
          ? data.lastUpdated.toDate()
          : typeof data.lastUpdated === 'string' || typeof data.lastUpdated === 'number'
            ? new Date(data.lastUpdated)
            : undefined
        : undefined,
    };
  } catch (error) {
    console.error('[LocationSharing] Error getting active location share:', error);
    return null;
  }
};

/**
 * Subscribe to active location share
 */
export const subscribeToActiveLocationShare = (
  userId: string,
  callback: (share: LocationShare | null) => void
): Unsubscribe => {
  const sharesRef = collection(db, 'locationShares');
  const sharesQuery = query(
    sharesRef,
    // IMPORTANT: Avoid subscribing to the entire collection, which can cause
    // permission errors when Firestore rules restrict access.
    // This also avoids loading unrelated docs.
    where('userId', '==', userId),
    limit(10)
  );
  
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;

  try {
    unsubscribeFn = onSnapshot(
      sharesQuery,
      (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }

        try {
          const activeShare =
            snapshot.docs
              .map((docSnap) => {
                const data = docSnap.data() as FirestoreLocationShare;
                if (data.userId !== userId) {
                  return null;
                }
                return locationShareFromFirestoreDoc(docSnap.id, data);
              })
              .find((share) => share !== null) || null;

          callback(activeShare);
        } catch (error) {
          console.error('[LocationSharing] Error processing snapshot:', error);
          if (!isCleanedUp) {
            try {
              callback(null);
            } catch (callbackError) {
              console.error('[LocationSharing] Error in callback:', callbackError);
            }
          }
        }
      },
      (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }

        console.error('[LocationSharing] Error subscribing to location share:', error);
        // For permission errors, don't call callback to avoid cascading errors
        const code = String((error as { code?: string })?.code ?? '');
        if (code !== 'permission-denied' && code !== 'missing-or-insufficient-permissions') {
          try {
            callback(null);
          } catch (callbackError) {
            console.error('[LocationSharing] Error in callback:', callbackError);
          }
        }
      }
    );
  } catch (error) {
    console.error('[LocationSharing] Error setting up subscription:', error);
    // Return a no-op cleanup function if setup fails
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;

    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Subscribe to location shares where the current user is a recipient (in sharedWith).
 * Requires composite index: sharedWith (array-contains) + isActive.
 */
export const subscribeToIncomingLocationShares = (
  recipientUid: string,
  callback: (shares: LocationShare[]) => void
): Unsubscribe => {
  const sharesRef = collection(db, 'locationShares');
  const sharesQuery = query(
    sharesRef,
    where('sharedWith', 'array-contains', recipientUid),
    where('isActive', '==', true),
    limit(10)
  );

  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;

  try {
    unsubscribeFn = onSnapshot(
      sharesQuery,
      (snapshot) => {
        if (isCleanedUp) {
          return;
        }

        try {
          const shares: LocationShare[] = [];
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data() as FirestoreLocationShare;
            if (data.userId === recipientUid) {
              continue;
            }
            if (!data.sharedWith?.includes(recipientUid)) {
              continue;
            }
            const parsed = locationShareFromFirestoreDoc(docSnap.id, data);
            if (parsed) {
              shares.push(parsed);
            }
          }
          callback(shares);
        } catch (error) {
          console.error('[LocationSharing] Error processing incoming shares snapshot:', error);
          if (!isCleanedUp) {
            try {
              callback([]);
            } catch (callbackError) {
              console.error('[LocationSharing] Error in callback:', callbackError);
            }
          }
        }
      },
      (error) => {
        if (isCleanedUp) {
          return;
        }

        console.error('[LocationSharing] Error subscribing to incoming location shares:', error);
        const code = String((error as { code?: string })?.code ?? '');
        if (code !== 'permission-denied' && code !== 'missing-or-insufficient-permissions') {
          try {
            callback([]);
          } catch (callbackError) {
            console.error('[LocationSharing] Error in callback:', callbackError);
          }
        }
      }
    );
  } catch (error) {
    console.error('[LocationSharing] Error setting up incoming shares subscription:', error);
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return;
    }
    isCleanedUp = true;

    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch {
        // ignore
      } finally {
        unsubscribeFn = null;
      }
    }
  };
};

